/**
 * Large file size threshold for iOS (256 MB).
 * Mobile Safari has strict memory limits — loading the full blob into memory
 * and then creating a File object from it effectively doubles memory usage.
 * Files above this threshold will skip the Web Share API path on iOS.
 */
import { Logger } from 'src/utils/logger';

const IOS_LARGE_FILE_THRESHOLD = 256 * 1024 * 1024;

const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false; // SSR guard
  const ua = navigator.userAgent;
  // navigator.userAgentData is the modern replacement for navigator.platform (deprecated)
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS detection: Safari reports 'MacIntel', Chromium reports 'macOS'
    ((platform === 'MacIntel' || platform === 'macOS') && navigator.maxTouchPoints > 1)
  );
};

const downloadViaBlob = (blob: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = blobUrl;
  anchor.download = filename;

  document.body.append(anchor);
  anchor.click();

  setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * Downloads a file from a URL.
 *
 * On iOS, uses the Web Share API (native "Save to Files" sheet) since
 * the <a download> attribute is ignored for blob URLs in most iOS browsers.
 *
 * Returns { shared: true } when iOS share sheet was used (file already saved),
 * or { shared: false } for blob downloads (file download completed by browser).
 *
 * Throws on failure so callers can show error toasts.
 * On iOS, always throws on fetch failure (anchor fallback doesn't work on iOS).
 * On non-iOS, falls back to anchor-based download before throwing.
 */
const download = async (
  url: string,
  customFilename?: string,
): Promise<{ shared: boolean }> => {
  const filename = customFilename || getFilenameFromUrl(url);

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();

    // On iOS, the <a download> attribute is ignored for blob URLs in many browsers.
    // Use the Web Share API with a File object to trigger the native "Save to Files" sheet.
    if (isIOS() && navigator.canShare && blob.size < IOS_LARGE_FILE_THRESHOLD) {
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
        } catch (shareError) {
          // User dismissed the share sheet — not an error, just bail silently
          if (shareError instanceof Error && shareError.name === 'AbortError') {
            return { shared: true };
          }
          throw shareError;
        }
        return { shared: true };
      }
    }

    downloadViaBlob(blob, filename);
    return { shared: false };
  } catch (error) {
    // On iOS, anchor fallback doesn't work (<a download> is ignored) — re-throw
    // so callers can show an error toast
    if (isIOS()) {
      throw error;
    }

    // Non-iOS: try anchor fallback as last resort.
    // This may not trigger a save-as dialog for cross-origin URLs, but it won't be blocked.
    Logger.warn('Download via fetch failed, falling back to direct link', 'download', { url, error });

    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = filename;

    document.body.append(anchor);
    anchor.click();

    setTimeout(() => {
      anchor.remove();
    }, 1000);

    // Re-throw so callers know the primary download failed
    // (anchor fallback is best-effort and may not trigger a save dialog for cross-origin URLs)
    throw error;
  }
};

const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    // decodeURIComponent may produce slashes from %2F in S3 paths — extract final segment
    const decoded = decodeURIComponent(lastSegment);
    return decoded.split('/').pop() || 'download';
  } catch {
    return 'download';
  }
};

const getExtensionFromUrl = (url: string): string => {
  try {
    // new URL().pathname already strips query params
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    const dotIndex = lastSegment.lastIndexOf('.');
    return dotIndex !== -1 ? lastSegment.slice(dotIndex) : '';
  } catch {
    return '';
  }
};

export { download, getExtensionFromUrl };
